import { supabaseClient } from '@auth-v2';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { fetchActivityCount, fetchAllUserActivities } from '@data';

import { queryKeys } from '../queryKeys';

const ACTIVITIES_PAGE_SIZE = 20;

export type Filter = {
    keywords: string;
    startDate: Date | null;
    endDate: Date | null;
    favorites: boolean;
    clubId: number | undefined;
    clubTypeId: number | undefined;
    hasClub: boolean | undefined;
    studentId: string;
    boothId: number | undefined;
    tags: number[] | undefined;
};

export const useFetchAllUserActivities = (filter?: Partial<Filter>, userId?: string) => {
    return useInfiniteQuery({
        queryKey: queryKeys.activities.filtered(userId || '', filter),
        queryFn: ({ pageParam = 0 }) =>
            fetchAllUserActivities({
                userId,
                offset: pageParam,
                limit: ACTIVITIES_PAGE_SIZE,
                clubId: filter?.clubId ?? null,
                clubTypeId: filter?.clubTypeId ?? null,
                hasClub: filter?.hasClub ?? null,
                startTime: convertDateToTimestamp(filter?.startDate),
                endTime: convertDateToTimestamp(filter?.endDate),
                hasFavorite: filter?.favorites ?? null,
                favoriteText: filter?.keywords ?? null,
                boothId: filter?.boothId ?? null,
                tags: filter?.tags?.length ? filter.tags : null,
            }),
        getNextPageParam: (lastPage, allPages) => {
            // if the last load was a full batch continue loading
            if (lastPage.length === ACTIVITIES_PAGE_SIZE) {
                return allPages.flat().length;
            }
            // else if we did not get a full batch we stop loading
            return undefined;
        },
        initialPageParam: 0,
    });
};

function convertDateToTimestamp(date: Date | undefined | null): string | null {
    if (!date) {
        return null;
    }
    return format(date, 'yyyy-MM-dd');
}

export const useGetActivityCount = (userId: string) => {
    return useQuery({
        queryKey: queryKeys.activities.count(userId),
        queryFn: async () => fetchActivityCount(supabaseClient, userId!),
        enabled: !!userId,
    });
};
